import { createContext, useContext, useEffect, useRef, useState } from "react";
import { getWSUrl } from "../utils/ws";
import { API_BASE_URL } from "../api/http";

// ⚙️ این کانتکست تنها مسئول برقراری اتصال وب‌ساکت هست.
// AdminDashboard و TicketNotificationPopup هر دو از اینجا پیام می‌خونن،
// دیگه هیچ‌کدوم خودشون یه اتصال WebSocket جدا باز نمی‌کنن.
const WS_URL = getWSUrl("/ws/support/notifications/");
const socket = new WebSocket(WS_URL);

const TicketSocketContext = createContext(null);

export function TicketSocketProvider({ children }) {
  // هر پیام جدید به شکل { data, receivedAt } ذخیره می‌شه؛
  // receivedAt باعث می‌شه حتی اگه دو پیام محتوای یکسان داشته باشن،
  // مصرف‌کننده‌ها (useEffect ها) بازم برای هر پیام جداگانه اجرا بشن.
  const [lastMessage, setLastMessage] = useState(null);

  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    const MAX_ATTEMPTS = 6; // بعد از این تعداد تلاش ناموفق، دیگه خودکار retry نمی‌کنه
    const BASE_DELAY = 3000; // شروع از ۳ ثانیه
    const MAX_DELAY = 30000; // و حداکثر تا ۳۰ ثانیه فاصله می‌گیره

    const connect = () => {
      if (cancelled) return;
      // اگه یه اتصال باز/در حال باز شدن از قبل هست، دوباره نساز
      if (
        wsRef.current &&
        (wsRef.current.readyState === WebSocket.OPEN ||
          wsRef.current.readyState === WebSocket.CONNECTING)
      ) {
        return;
      }

      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        attemptsRef.current = 0; // اتصال موفق بود، شمارنده‌ی تلاش‌ها صفر بشه
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setLastMessage({ data, receivedAt: Date.now() });
        } catch (err) {
          // پیام غیر JSON یا فرمت نامعتبر - نادیده گرفته می‌شه
          console.warn("پیام وب‌ساکت قابل پردازش نبود:", err);
        }
      };

      ws.onclose = () => {
        if (cancelled) return;
        attemptsRef.current += 1;
        if (attemptsRef.current > MAX_ATTEMPTS) {
          console.warn(
            "اتصال وب‌ساکت تیکت‌ها بعد از چند تلاش ناموفق متوقف شد. آدرس WS_URL و در دسترس بودن سرور رو بررسی کن.",
          );
          return; // دیگه دوباره تلاش نمی‌کنه تا خود پروایدر remount بشه
        }
        // فاصله‌ی تلاش بعدی به صورت نمایی زیاد می‌شه: ۳، ۶، ۱۲، ۲۴، ۳۰، ۳۰...
        const delay = Math.min(
          BASE_DELAY * 2 ** (attemptsRef.current - 1),
          MAX_DELAY,
        );
        reconnectTimerRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        ws.close();
      };
    };

    connect();

    return () => {
      cancelled = true;
      clearTimeout(reconnectTimerRef.current);
      wsRef.current?.close();
    };
  }, []);

  return (
    <TicketSocketContext.Provider value={lastMessage}>
      {children}
    </TicketSocketContext.Provider>
  );
}

// هوک مصرف: هر بار یه پیام جدید از سرور برسه، مقدار جدیدی برمی‌گردونه
export function useTicketSocket() {
  return useContext(TicketSocketContext);
}
